import React, { useState } from 'react';
import { Button, Modal, Spinner } from "@tag/tag-components-react-v4";
import { ACCTermination } from "../types/ACCTermination";
import LockSiteService from "../services/LockSiteService";
import { ProjectAccents } from "../../General/config/projectAccents";
import PipelineStatusDisplay from "../../General/components/PipelineStatusDisplay";

interface DeleteSiteResult {
    buildUrl?: string;
    buildId?: number;
    pipelineId?: number;
}

function DeleteSiteModal(props: Readonly<{
    visible: boolean,
    selectedTermination: ACCTermination,
    handleClose: () => void,
    setAlert: Function
}>) {

    const [loading, setLoading] = useState(false);
    const [pipelineResult, setPipelineResult] = useState<DeleteSiteResult>();

    const handleConfirmDelete = async () => {
        setLoading(true);

        try {
            const result = await LockSiteService.deleteSite(
                props.selectedTermination.appGroupName,
                Number(props.selectedTermination.clientID)
            );

            if (result.success) {
                setPipelineResult({
                    buildUrl: result.buildUrl,
                    buildId: result.buildId,
                    pipelineId: result.pipelineId
                });
            } else {
                props.setAlert({ type: 'error', message: result.errorMessage || 'Failed to trigger delete site pipeline' });
                props.handleClose();
            }
        } catch (e: any) {
            props.setAlert({ type: 'error', message: e.message || 'An error occurred, please contact HSC DevOps' });
            props.handleClose();
        } finally {
            setLoading(false);
        }
    };

    const renderModalContent = () => {
        if (loading) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1em', padding: '1em 0' }}>
                    <Spinner />
                    <span>Triggering delete site pipeline...</span>
                </div>
            );
        }

        if (pipelineResult) {
            return (
                <>
                    <p>Pipeline triggered successfully for <strong>{props.selectedTermination.clientName}</strong>!</p>
                    {pipelineResult.pipelineId && pipelineResult.buildId ? (
                        <PipelineStatusDisplay
                            apiBaseUrl={process.env.REACT_APP_SELF_SERVICE_CARECOMPLIANCE_API_URL}
                            pipelineId={pipelineResult.pipelineId}
                            runId={pipelineResult.buildId}
                            buildUrl={pipelineResult.buildUrl}
                        />
                    ) : pipelineResult.buildUrl ? (
                        <p>
                            <a href={pipelineResult.buildUrl} target="_blank" rel="noopener noreferrer">
                                View Pipeline Run
                            </a>
                        </p>
                    ) : pipelineResult.buildId ? (
                        <p>Pipeline Run ID: <strong>{pipelineResult.buildId}</strong></p>
                    ) : null}
                </>
            );
        }

        return (
            <>
                <p>Are you sure you want to delete site <strong>{props.selectedTermination.clientName}</strong> ({props.selectedTermination.clientID} - {props.selectedTermination.appGroupName})?</p>
                <p><strong>This action cannot be undone.</strong></p>
                <div style={{ display: 'flex', gap: '1em', marginTop: '1em' }}>
                    <Button text={'Confirm Delete'} onClick={handleConfirmDelete} />
                </div>
            </>
        );
    };

    return (
        <Modal
            visible={props.visible}
            accent={ProjectAccents.CareCompliance}
            heading={pipelineResult ? 'Delete Site Triggered' : 'Delete Site'}
            onCancelButtonClick={() => props.handleClose()}
            onClose={() => props.handleClose()}
            primaryButtonProps={{ buttonStyle: { 'display': 'none' } }}
        >
            <div>
                {renderModalContent()}
            </div>
        </Modal>
    );
}

export default DeleteSiteModal;
